// One place that holds a checkout submit while the cart is checked.
//
// This used to live inside stlukes-gwp.js, which only loads while a gift
// campaign is running. That was fine when the gift was the only thing anyone
// checked. It is not fine now: with no campaign active, that file is not on the
// page, and the purchase caps would have had no gate at all.
//
// Two gates on the same submit event cannot work. The first one releases with
// requestSubmit, which fires submit again, and the second has no way to tell
// that from a fresh press. So there is one gate, and anything that needs a look
// at the cart registers a check:
//
//   window.StLukesCheckoutChecks = window.StLukesCheckoutChecks || [];
//   window.StLukesCheckoutChecks.push(async () => 'message' | null);
//
// A check returns the sentence to stop the shopper with, or nothing to let them
// through. It is expected to have already put the cart right; the message says
// what changed.
//
// It is a gate, not a guarantee. Enforcement in the browser can always be
// outrun, and a cart assembled with JavaScript off has never been through it at
// all. Only a Shopify Function validating the cart at checkout is real
// enforcement.
(() => {
  const CART_WRITES = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const nativeFetch = window.fetch;

  // Cart writes sent but not yet answered. The gate has to wait for these: a
  // shopper who presses minus and then Checkout immediately would otherwise
  // have the gate read a cart that has not caught up, find it perfectly valid,
  // and wave them through seconds before the change lands.
  let writesInFlight = 0;

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const result = nativeFetch.apply(this, arguments);

    if (CART_WRITES.test(String(url).split('?')[0])) {
      writesInFlight += 1;
      const settle = () => {
        writesInFlight -= 1;
        // Anything that wants to react to a cart change listens for this rather
        // than patching fetch a second time.
        document.dispatchEvent(new CustomEvent('stlukes:cart-write'));
      };
      result.then(settle).catch(settle);
    }

    return result;
  };

  // Nothing in flight, or long enough has passed that waiting further is worse
  // than proceeding.
  const cartSettled = async (limitMs = 3000) => {
    const until = Date.now() + limitMs;
    while (writesInFlight > 0 && Date.now() < until) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  const say = (message) => {
    for (const el of document.querySelectorAll('[data-cart-notice]')) {
      el.textContent = message || '';
      el.hidden = !message;
    }
  };

  let releasing = false;

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || releasing) return;

      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      const checks = window.StLukesCheckoutChecks || [];
      if (!checks.length) return;

      event.preventDefault();
      submitter.disabled = true;

      let message = null;
      try {
        await cartSettled();

        for (const check of checks) {
          // Capped, because a checkout button that never releases is worse than
          // anything a check is guarding against.
          const said = await Promise.race([
            Promise.resolve().then(check),
            new Promise((resolve) => setTimeout(() => resolve(null), 6000))
          ]);
          if (said) {
            message = said;
            break;
          }
        }
      } catch (e) {
        // A broken check must not be able to bar the door.
        message = null;
      }

      submitter.disabled = false;

      if (message) {
        say(message);
        return;
      }

      // requestSubmit fires the submit event synchronously, so the flag only
      // has to hold for the length of that call. Left set, it would let the
      // first checkout through and then stand down for the rest of the page.
      releasing = true;
      form.requestSubmit(submitter);
      releasing = false;
    },
    true
  );

  window.StLukesCheckoutGate = { cartSettled };
})();
